import { isEmpty } from 'lodash'
import i18n from 'translations/i18n'
import { useCallback, useEffect, useMemo, useState } from 'react'
import FilterBox from '../FilterBox'
import { useFilters } from './context'
import useFilterAPI from '../useFilterAPI'
import { returnBadgeList } from '../helper'

const CANDIDATE_JOB_MATCHING_DEFAULT_FILTER = ['all']

type CandidateJobMatchingScoreProps = {
  filterKey: string
  filters: Array<string>
}

const CandidateJobMatchingScore = ({ filterKey, filters }: CandidateJobMatchingScoreProps) => {
  const { matching_score } = useFilterAPI()
  const { filter, setFilter } = useFilters()
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const optionList = useMemo(() => returnBadgeList(matching_score), [matching_score])

  const handleAddValue = useCallback(
    (storedScores: string[], valueToAdd: string) => {
      const newScores = [...storedScores, valueToAdd]

      if (newScores.length === matching_score.length - 1) {
        setFilter((oldFilter: any) => ({
          ...oldFilter,
          [filterKey]: CANDIDATE_JOB_MATCHING_DEFAULT_FILTER,
        }))
        return
      }

      setFilter((oldFilter: any) => ({ ...oldFilter, [filterKey]: newScores }))
    },
    [filterKey, matching_score],
  )

  const handleRemoveValue = useCallback(
    (storedScores: string[], valueToRemove: string) => {
      const newScores = [...storedScores]
      const index = newScores.indexOf(valueToRemove)
      newScores.splice(index, 1)

      if (newScores.length === 0) {
        setFilter((oldFilter: any) => ({
          ...oldFilter,
          [filterKey]: CANDIDATE_JOB_MATCHING_DEFAULT_FILTER,
        }))
        return
      }

      setFilter((oldFilter: any) => ({ ...oldFilter, [filterKey]: newScores }))
    },
    [filterKey],
  )
  const handleSelectAll = useCallback(() => {
    setFilter((oldFilter: any) => ({
      ...oldFilter,
      [filterKey]: CANDIDATE_JOB_MATCHING_DEFAULT_FILTER,
    }))
  }, [filterKey])

  const handleFilterChange = useCallback(
    (__: string, val: string | boolean) => {
      const value = val as string
      if (value === 'all') {
        handleSelectAll()
      } else {
        const storedScores = [...filter[filterKey]]

        // If 'all' filter is already selected, remove it from the list
        if (storedScores.includes('all')) {
          storedScores.splice(storedScores.indexOf('all'), 1)
        }
        const index = storedScores.indexOf(value)

        // If the value is already in the list, remove it
        if (index >= 0) {
          handleRemoveValue(storedScores, value)
          return
        }

        // Otherwise, add the value to the list
        handleAddValue(storedScores, value)
      }
    },
    [
      filterKey,
      filter[filterKey],
      matching_score,
      handleSelectAll,
      handleAddValue,
      handleRemoveValue,
    ],
  )

  useEffect(() => {
    if (!isEmpty(filter[filterKey])) {
      setSelectedIds(filter[filterKey])
    } else {
      setSelectedIds(CANDIDATE_JOB_MATCHING_DEFAULT_FILTER)
    }
  }, [filter[filterKey]])

  return (
    <FilterBox
      handleFilter={handleFilterChange}
      options={optionList}
      valueKey="id"
      isClearable={false}
      labelKey="label"
      selectedIds={selectedIds as never[]}
      componentType="badges"
      filterKey={filterKey}
      filters={filters}
      title={i18n.t('label.candidate_score')}
    />
  )
}

export default CandidateJobMatchingScore
